import { ArrowUpRight, CalendarRange, ScanSearch, ShieldCheck } from "lucide-react";
import { Button } from "@nexus/ui";
import {
  CampaignLiveDot as LiveDot,
  campaignTitleCase as titleCase,
  formatDateTime,
  formatDetectionDomain,
  formatResponseType,
  isEventLive,
  PopularityMeter,
  type CampaignMatrixCell,
  type CampaignMatrixEngineRow,
  type CampaignMatrixEvent,
} from "@nexus/widget-kit";
import { DEFAULT_EVENT_PROTECTION_LABELS, type EventProtectionLabels } from "./labels";

function formatOffset(minutes: number): string {
  return `${minutes >= 0 ? "+" : ""}${minutes}m`;
}

function normalizeEngineIdentity(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9]/g, "");
}

function EngineIdentity({ engine }: { engine: CampaignMatrixEngineRow }) {
  const showGroup =
    normalizeEngineIdentity(engine.engine_group_label) !==
    normalizeEngineIdentity(engine.engine_label);

  return (
    <div className="min-w-32 space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-semibold leading-5 text-primary">{engine.engine_label}</span>
        {!engine.is_nagra && (
          <span className="inline-flex rounded bg-surface-elevated px-1.5 py-0.5 text-xs font-medium leading-3 text-muted">
            Third-party
          </span>
        )}
      </div>
      {showGroup && (
        <div className="text-xs font-normal leading-4 text-muted">{engine.engine_group_label}</div>
      )}
    </div>
  );
}

function CellDetail({
  cell,
  engineType,
}: {
  cell: CampaignMatrixCell;
  engineType: "detection" | "response";
}) {
  const chipTone = engineType === "detection" ? "bg-data-detection/10" : "bg-data-response/10";

  if (engineType === "response") {
    return (
      <div className="space-y-1 text-xs leading-4">
        {cell.response_types.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {cell.response_types.map((value) => (
              <span
                key={value}
                className={`rounded px-1 py-0.5 font-medium text-secondary ${chipTone}`}
              >
                {formatResponseType(value)}
              </span>
            ))}
          </div>
        )}
        {cell.target_attributes.length > 0 && (
          <div className="text-muted">{cell.target_attributes.map(titleCase).join(", ")}</div>
        )}
        {(cell.response_start_diff != null || cell.response_end_diff != null) && (
          <div
            className="font-medium tabular-nums text-muted"
            title="Response start offset from event start to response stop offset from event end"
          >
            Window {cell.response_start_diff != null ? formatOffset(cell.response_start_diff) : "—"}
            {" ⇔ "}
            {cell.response_end_diff != null ? formatOffset(cell.response_end_diff) : "—"}
          </div>
        )}
        {cell.isp_count > 0 && (
          <div className="font-medium tabular-nums text-muted">{cell.isp_count} ISPs</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1 text-xs leading-4">
      {cell.infringement_types.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {cell.infringement_types.map((value) => (
            <span
              key={value}
              className={`rounded px-1 py-0.5 font-medium text-secondary ${chipTone}`}
            >
              {titleCase(value)}
            </span>
          ))}
        </div>
      )}
      {cell.monitored_plateforms.length > 0 && (
        <div className="text-muted">
          {cell.monitored_plateforms.map(formatDetectionDomain).join(", ")}
        </div>
      )}
      {cell.harvesting_window_minutes.length > 0 && (
        <div className="tabular-nums text-muted">
          {cell.harvesting_window_minutes.join("/")} min window
        </div>
      )}
    </div>
  );
}

function EngineRow({
  engine,
  columns,
}: {
  engine: CampaignMatrixEngineRow;
  columns: CampaignMatrixEvent["columns"];
}) {
  const activeCellTone =
    engine.engine_type === "detection" ? "bg-data-detection/5" : "bg-data-response/5";

  return (
    <tr className="border-t border-border">
      <th scope="row" className="sticky left-0 z-10 bg-surface px-4 py-3 text-left align-top">
        <EngineIdentity engine={engine} />
      </th>
      {columns.map((column) => {
        const cell = engine.cells[column.key];
        return (
          <td
            key={column.key}
            className={`border-l border-border px-4 py-3 align-top ${cell ? activeCellTone : ""}`}
          >
            {cell ? (
              <div>
                <CellDetail cell={cell} engineType={engine.engine_type} />
              </div>
            ) : (
              <span className="text-xs text-muted" title="Not enabled" aria-label="Not enabled">
                —
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function GroupHeaderRow({
  label,
  span,
  tone,
}: {
  label: string;
  span: number;
  tone: "detection" | "response";
}) {
  const Icon = tone === "detection" ? ScanSearch : ShieldCheck;
  const rowTone =
    tone === "detection"
      ? "border-data-detection/30 bg-data-detection/10"
      : "border-data-response/30 bg-data-response/10";
  const iconTone = tone === "detection" ? "text-data-detection" : "text-data-response";

  return (
    <tr>
      <td
        colSpan={span}
        className={`sticky left-0 border-t px-3 py-1.5 text-sm font-semibold text-primary ${rowTone}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <Icon className={`size-3.5 ${iconTone}`} aria-hidden />
          {label}
        </span>
      </td>
    </tr>
  );
}

export function EventProtection({
  event,
  nowMs,
  onOpenLiveEvents,
  labels = DEFAULT_EVENT_PROTECTION_LABELS,
}: {
  labels?: EventProtectionLabels;
  event: CampaignMatrixEvent | null;
  nowMs: number;
  onOpenLiveEvents?: (tenantId: string, tenantLabel: string) => void;
}) {
  if (!event) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
        <p className="text-sm font-medium text-secondary">{labels.emptyTitle}</p>
        <p className="text-xs text-muted">{labels.emptyDescription}</p>
      </div>
    );
  }

  const detectionEngines = event.engines.filter((engine) => engine.engine_type === "detection");
  const responseEngines = event.engines.filter((engine) => engine.engine_type === "response");
  const isLive = isEventLive(event.event_start, event.event_end, nowMs);

  return (
    <div className="h-full p-3">
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border bg-surface-elevated px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="break-words text-base font-semibold text-primary">
              {event.event_label}
            </span>
            {isLive && <LiveDot />}
            <PopularityMeter value={event.popularity} />
          </div>
          <div className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted">
            <CalendarRange className="size-3.5 text-brand-cyan" aria-hidden />
            <span className="font-medium tabular-nums text-secondary">
              {formatDateTime(event.event_start)} → {formatDateTime(event.event_end)}
            </span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {event && event.columns.length > 0 ? (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-20 border-b border-border bg-surface-elevated px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.engine}
                  </th>
                  {event.columns.map((column, columnIndex) => (
                    <th
                      key={column.key}
                      className="sticky top-0 z-10 border-b border-l border-border bg-surface-elevated px-4 py-3 text-left"
                    >
                      <div className="text-sm font-semibold text-primary">
                        {column.tenant_label}
                      </div>
                      <div className="text-xs text-muted">{column.channel}</div>
                      {onOpenLiveEvents &&
                      event.columns.findIndex(
                        (candidate) => candidate.tenant_id === column.tenant_id,
                      ) === columnIndex ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-1.5 -ml-1.5 px-1.5 text-brand-cyan hover:text-brand-cyan"
                          onClick={() => onOpenLiveEvents(column.tenant_id, column.tenant_label)}
                        >
                          {labels.monitor}
                          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detectionEngines.length > 0 && (
                  <>
                    <GroupHeaderRow
                      label={labels.detection}
                      span={event.columns.length + 1}
                      tone="detection"
                    />
                    {detectionEngines.map((engine) => (
                      <EngineRow key={engine.engine_id} engine={engine} columns={event.columns} />
                    ))}
                  </>
                )}
                {responseEngines.length > 0 && (
                  <>
                    <GroupHeaderRow
                      label={labels.response}
                      span={event.columns.length + 1}
                      tone="response"
                    />
                    {responseEngines.map((engine) => (
                      <EngineRow key={engine.engine_id} engine={engine} columns={event.columns} />
                    ))}
                  </>
                )}
              </tbody>
            </table>
          ) : (
            <p className="p-3 text-xs text-muted">{labels.noEngineConfig}</p>
          )}
        </div>
      </div>
    </div>
  );
}
